import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, SliceProps } from '../store';
import { urlParamReplicaId, urlProjectLogOpen } from '../../Constants';
import { apiFetch } from '../../util/ProjectUtils';

export const databaseAccessReasons = [
    'First Level Support',
    'Vorgang Support',
    'Vorgang Entwicklung',
    'Fehlersuche',
    'Fehlerbehebung',
    'Code prüfen',
    'Consulting',
    'Qualitätsprüfung',
    'Schulung',
    'Dokument Prüfung',
    'Konfigurationsprüfung',
    'Update',
    'Incident (Dienstverweigerungsangriff (DDoS))',
    'Incident (Falschmeldung)',
    'Incident (Fehlerhafte Konfiguration)',
    'Incident (Fremdes Eindringen)',
    'Incident (Irrläufer)',
    'Incident (Scannen des Netzwerks auf Suche nach möglichen Schwachstellen)',
    'Incident (Sicherheitsverletzung auf Admin-Ebene)',
    'Incident (Social Engineering)',
    'Incident (System Missbrauch)',
    'Incident (Technische Schwachstelle)',
    'Incident (Unerlaubte Änderung)',
    'Incident (Unerlaubte Weitergabe von Informationen)',
    'Incident (Unbekannt)',
    'Incident (Benutzerkonto kompromittiert)',
    'Incident (Virus/Schadcode Website)',
    'Incident (Unberechtigte Veränderung der Internetseite)',
];

/**
 * Initial slice state value.
 */
const initialState: IDatabaseAccessState = {
    isDialogOpen: false,
    canAccessDatabase: false,
    loading: false,
    error: null,
};

const reducers = {
    toggleDatabaseAccessDialog(state: IDatabaseAccessState, action: IAction<boolean>) {
        state.isDialogOpen = action.payload;
    },
    resetErrorPostProjectAccessLog(state: IDatabaseAccessState) {
        state.error = null;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Get is project log open for Database Access Dialog
     */
    getProjectLogOpen: async (config: { replicaId: string }): Promise<string[]> => {
        const { replicaId } = config;
        try {
            const url = urlProjectLogOpen.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            if (response.status === 204) {
                return null;
            }

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    postProjectAccessLog: async (config: {
        reason: string;
        comment: string;
        replicaId: string;
    }): Promise<void> => {
        const { replicaId, reason, comment } = config;

        try {
            const url = urlProjectLogOpen.replace(urlParamReplicaId, replicaId);
            const formData = new FormData();
            formData.append('reason', reason);
            formData.append('comment', comment);

            const response = await apiFetch(url, {
                method: 'POST',
                body: formData,
            });

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IDatabaseAccessState>) => {
        /**
         * Reducers for the 'getProjectLogOpen' thunk.
         */
        builder.addCase(getProjectLogOpen.pending, (state: Draft<IDatabaseAccessState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectLogOpen.fulfilled,
            (state: Draft<IDatabaseAccessState>, action: IAction<string[]>) => {
                if (action.payload) {
                    state.isDialogOpen = true;
                } else {
                    state.canAccessDatabase = true;
                }

                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectLogOpen.rejected,
            (state: Draft<IDatabaseAccessState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
        /**
         * Reducers for the 'onConfirmReasonAccessDatabase' thunk.
         */
        builder.addCase(postProjectAccessLog.pending, (state: Draft<IDatabaseAccessState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(postProjectAccessLog.fulfilled, (state: Draft<IDatabaseAccessState>) => {
            state.canAccessDatabase = true;
            state.loading = false;
            state.error = null;
        });
        builder.addCase(
            postProjectAccessLog.rejected,
            (state: Draft<IDatabaseAccessState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const postProjectAccessLog = createAsyncThunk(
    `${urlProjectLogOpen}-post`,
    thunks.postProjectAccessLog,
);

export const getProjectLogOpen = createAsyncThunk(
    `${urlProjectLogOpen}-get`,
    thunks.getProjectLogOpen,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IDatabaseAccessState extends SliceProps {
    isDialogOpen: boolean;
    canAccessDatabase: boolean;
}

/**
 * Create the redux slice.
 */
export const databaseAccessSlice = createSlice({
    name: 'databaseAccess',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { toggleDatabaseAccessDialog, resetErrorPostProjectAccessLog } =
    databaseAccessSlice.actions;

/**
 * Export slice reducer as default.
 */
export default databaseAccessSlice.reducer;
